import { request } from "src/apis/index";
import { getCsrfHeaders } from "src/utils/csrf";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Localizacao = { id: number; city: string; state: string };
export type LocalizacaoPayload = { city: string; state: string };

export type Abordagem = { id: number; approach: string };
export type AbordagemPayload = { approach: string };

export type PsicologoEF = {
  id: number;
  name: string;
  title: string;
  description: string;
  therapy_meaning?: string;
  contact?: string;
  location?: string;
  approach?: string;
  online_service?: string;
  human?: string;
  photo_url?: string;
  topics?: string[];
  is_active: boolean;
};
export type PsicologoEFPayload = Omit<PsicologoEF, "id" | "photo_url">;

export type UsuarioFamily = {
  id: number;
  created_at?: string;
  name: string;
  surname?: string;
  email: string;
  phone?: string;
  document?: string;
  birthday?: string;
  postal_code?: string;
  add_number?: number;
  complement?: string;
  role?: string;
  token?: string;
  li_termos?: boolean;
  company_id?: number;
  collaborator_id?: number;
  dados_assinatura?: object | null;
};

export type EmpresaPortal = { id: number; trade_name: string };
export type ColaboradorPortal = { id: number; name: string };

export type NotificacaoFamilyTarget =
  | "ALL_COLLABORATORS"
  | "COMPANY_COLLABORATORS"
  | "ONE_COLLABORATOR";

export type NotificacaoFamilyPayload = {
  target: NotificacaoFamilyTarget;
  company_id?: number;
  collaborator_id?: number;
  subject: string;
  title: string;
  message: string;
  token: string | null;
};

export type RelatorioFamilyFilters = {
  start: string;
  end: string;
  comp_start?: string | null;
  comp_end?: string | null;
  company_id?: number | null;
  locale?: string;
};

export type RelatorioPeriod = {
  totals: {
    paid_count: number;
    paid_sum_cents: number;
    unpaid_count: number;
    unpaid_sum_cents: number;
  };
  by_payment_method: { method: string; count: number; total_cents: number }[];
  by_company: {
    company: string;
    count: number;
    total_cents: number;
    paying_collaborators: number;
  }[];
  delinquency_by_company: {
    company: string;
    unpaid_count: number;
    unpaid_sum_cents: number;
    delinquency_rate: number;
  }[];
};

export type RelatorioFamilyData = {
  current: RelatorioPeriod;
  comparison?: RelatorioPeriod;
};

export type RelatorioFamilySendPayload = RelatorioFamilyFilters & {
  to: string[];
  cc?: string[];
  subject: string;
  attach_csv: boolean;
};

// ─── Localizações ─────────────────────────────────────────────────────────────

export const getLocalizacoes = async (): Promise<Localizacao[]> => {
  const res = await request.get<Localizacao[] | { data: Localizacao[] }>("/admin/localizacoes");
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const createLocalizacao = async (
  payload: LocalizacaoPayload
): Promise<void> => {
  await request.post("/admin/localizacoes", payload, {
    headers: getCsrfHeaders(),
  });
};

export const updateLocalizacao = async (
  id: number,
  payload: LocalizacaoPayload
): Promise<void> => {
  await request.put(`/admin/localizacoes/${id}`, payload, {
    headers: getCsrfHeaders(),
  });
};

export const deleteLocalizacao = async (id: number): Promise<void> => {
  await request.delete(`/admin/localizacoes/${id}`, {
    headers: getCsrfHeaders(),
  });
};

// ─── Abordagens ───────────────────────────────────────────────────────────────

export const getAbordagens = async (): Promise<Abordagem[]> => {
  const res = await request.get<Abordagem[] | { data: Abordagem[] }>("/admin/abordagens");
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const createAbordagem = async (
  payload: AbordagemPayload
): Promise<void> => {
  await request.post("/admin/abordagens", payload, {
    headers: getCsrfHeaders(),
  });
};

export const updateAbordagem = async (
  id: number,
  payload: AbordagemPayload
): Promise<void> => {
  await request.put(`/admin/abordagens/${id}`, payload, {
    headers: getCsrfHeaders(),
  });
};

export const deleteAbordagem = async (id: number): Promise<void> => {
  await request.delete(`/admin/abordagens/${id}`, {
    headers: getCsrfHeaders(),
  });
};

// ─── Psicólogos ───────────────────────────────────────────────────────────────

export const getPsicologosEF = async (): Promise<PsicologoEF[]> => {
  const res = await request.get<{ data: PsicologoEF[] } | PsicologoEF[]>(
    "/admin/psicologos"
  );
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const createPsicologoEF = async (
  payload: PsicologoEFPayload
): Promise<PsicologoEF> => {
  const res = await request.post<PsicologoEF | { data: PsicologoEF }>(
    "/admin/psicologos",
    payload,
    { headers: getCsrfHeaders() }
  );
  // API pode responder { data: {...} } ou o objeto direto — desempacotar para garantir o id.
  const created = (res.data as { data?: PsicologoEF })?.data ?? (res.data as PsicologoEF);
  return created;
};

export const updatePsicologoEF = async (
  id: number,
  payload: Partial<PsicologoEFPayload>
): Promise<void> => {
  await request.put(`/admin/psicologos/${id}`, payload, {
    headers: getCsrfHeaders(),
  });
};

export const deletePsicologoEF = async (id: number): Promise<void> => {
  await request.delete(`/admin/psicologos/${id}`, {
    headers: getCsrfHeaders(),
  });
};

export const uploadPsicologoPhoto = async (
  id: number,
  file: File
): Promise<{ url: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await request.post<{ url: string }>(
    `/admin/psicologos/${id}/photo`,
    fd,
    { headers: { ...getCsrfHeaders() } }
  );
  return res.data;
};

// ─── Usuários Family ──────────────────────────────────────────────────────────

export const searchUsuariosFamily = async (
  q: string
): Promise<UsuarioFamily[]> => {
  const res = await request.get<UsuarioFamily[] | { data: UsuarioFamily[] }>(
    "/legacy/all_users_family",
    { params: q ? { q } : undefined }
  );
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const getUserFamilyById = async (
  id: number
): Promise<UsuarioFamily> => {
  const res = await request.get<UsuarioFamily>(`/admin/portal/users/${id}`);
  return res.data;
};

export const createUsuarioFamily = async (
  payload: Partial<UsuarioFamily>
): Promise<void> => {
  await request.post("/legacy/users_family", payload, {
    headers: getCsrfHeaders(),
  });
};

export const updateUsuarioFamily = async (
  collaboratorId: number,
  payload: Partial<UsuarioFamily> & { enterprise?: number; target_company_id?: number }
): Promise<void> => {
  await request.put(`/legacy/users_family/${collaboratorId}`, payload, {
    headers: getCsrfHeaders(),
  });
};

export const deleteUsuarioFamily = async (
  collaboratorId: number
): Promise<void> => {
  await request.delete(`/legacy/users_family/${collaboratorId}`, {
    headers: getCsrfHeaders(),
  });
};

export const getEmpresasPortal = async (): Promise<EmpresaPortal[]> => {
  const res = await request.get<EmpresaPortal[] | { data: EmpresaPortal[] }>(
    "/admin/portal/empresas"
  );
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const getColaboradoresByEmpresa = async (
  companyId: number
): Promise<ColaboradorPortal[]> => {
  const res = await request.get(`/admin/portal/empresas/${companyId}/colaboradores`);
  const raw = Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
  return raw.map((c: any) => ({ id: c.id_colaborador ?? c.id, name: c.nome ?? c.name }));
};

export const getEmpresaByColaborador = async (
  colaboradorId: number
): Promise<{ id: number } | null> => {
  try {
    const res = await request.get(`/admin/portal/colaboradores/${colaboradorId}/empresa`);
    const d = res.data as any;
    return { id: d.id_empresa ?? d.id };
  } catch {
    return null;
  }
};

// ─── Notificações ─────────────────────────────────────────────────────────────

export const getEmpresasNotif = async (): Promise<EmpresaPortal[]> => {
  const res = await request.get<EmpresaPortal[] | { data: EmpresaPortal[] }>(
    "/empresas"
  );
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const getColaboradoresNotif = async (
  companyId: number,
  name?: string
): Promise<ColaboradorPortal[]> => {
  const res = await request.get<
    ColaboradorPortal[] | { data: ColaboradorPortal[] }
  >(`/empresas/${companyId}/colaboradores`, {
    params: name ? { collaborator_name: name } : undefined,
  });
  return Array.isArray(res.data) ? res.data : (res.data as any).data ?? [];
};

export const sendNotificacaoFamily = async (
  payload: NotificacaoFamilyPayload
): Promise<void> => {
  await request.post("/collaborators/notifications", payload, {
    headers: getCsrfHeaders(),
  });
};

// ─── Relatório Financeiro ─────────────────────────────────────────────────────

const normalizePeriod = (raw: any): RelatorioPeriod => ({
  totals: raw.totals,
  by_payment_method: (raw.by_method ?? []).map((m: any) => ({
    method: m.method,
    count: m.paid_count,
    total_cents: m.paid_sum_cents,
  })),
  by_company: (raw.by_company ?? []).map((c: any) => ({
    company: c.company_name,
    count: c.paid_count,
    total_cents: c.paid_sum_cents,
    paying_collaborators: c.employees_paid,
  })),
  delinquency_by_company: (raw.delinquency_by_company ?? []).map((d: any) => ({
    company: d.company_name,
    unpaid_count: d.unpaid_count,
    unpaid_sum_cents: d.unpaid_sum_cents,
    delinquency_rate: (d.delinquency_rate_percent ?? 0) / 100,
  })),
});

const cleanReportFilters = (filters: RelatorioFamilyFilters): Record<string, unknown> => {
  const out: Record<string, unknown> = { start: filters.start, end: filters.end };
  if (filters.comp_start) out.comp_start = filters.comp_start;
  if (filters.comp_end) out.comp_end = filters.comp_end;
  if (filters.company_id) out.company_id = filters.company_id;
  if (filters.locale) out.locale = filters.locale;
  return out;
};

export const previewRelatorioFamily = async (
  payload: RelatorioFamilyFilters
): Promise<RelatorioFamilyData> => {
  const res = await request.post<any>(
    "/legacy/report/financeiro/preview",
    cleanReportFilters(payload),
    { headers: getCsrfHeaders() }
  );
  const report = res.data?.report ?? res.data;
  return {
    current: normalizePeriod(report.current),
    comparison: report.previous ? normalizePeriod(report.previous) : undefined,
  };
};

export const sendRelatorioFamily = async (
  payload: RelatorioFamilySendPayload
): Promise<void> => {
  const { to, cc, subject, attach_csv, ...filters } = payload;
  const body: Record<string, unknown> = { ...cleanReportFilters(filters), to, subject, attach_csv };
  if (cc && cc.length > 0) body.cc = cc;
  await request.post("/legacy/report/financeiro/send", body, {
    headers: getCsrfHeaders(),
  });
};
